/**
 * generateItemListPDF(priceData, cabinetSelections)
 *
 * Generates an Item List PDF matching the Domain Cabinets layout.
 * Uses jsPDF (loaded via CDN or npm: `npm i jspdf`).
 *
 * @param {object} priceData   - normalized priceData from the API response
 *                               { total_price, linear_feet, price_per_lf,
 *                                 design_url, item_list_url,
 *                                 // raw fields also accepted:
 *                                 lines: [...] }
 * @param {object} cabinetSelections - { collection, door_color, grain,
 *                                       box_material, drawer_box }
 *                                     Populated from finalArray selections.
 */
export async function generateItemListPDF(priceData, cabinetSelections = {}) {
  // ── 1. Dynamically load jsPDF if not already present ──────────────────────
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const { jsPDF } = window.jspdf;

  // ── 2. Resolve cabinet style strings ──────────────────────────────────────
  const col        = cabinetSelections.collection   || '(Collection Name)';
  const doorColor  = cabinetSelections.door_color   || '(Color Name)';
  const grain      = cabinetSelections.grain        || '(Style & Grain Direction)';
  const boxMat     = cabinetSelections.box_material || '(Box Material Name)';
  const drawerBox  = cabinetSelections.drawer_box   || '(Drawer Box Type)';

  // ── 3. Pull line items from raw API response ───────────────────────────────
  // priceData may be raw (has .lines) or normalized (no .lines).
  // If normalized, we still render with the data we have.
  const lines = priceData?.lines || [];

  // ── 4. Build cabinet-style string helper ──────────────────────────────────
  const styleStr = (line) => {
    const m = line?.msg || {};
    const parts = [
      `Collection=${col}`,
      `Door Color=${doorColor}`,
      `Grain=${grain}`,
    ];
    if (m.cabinetboxmaterial_id) parts.push(`Box Material=${boxMat}`);
    if (m.drawerboxtype_id)      parts.push(`Drawer Box=${drawerBox}`);
    if (m.top_front_height)      parts.push(`Top Drawer Height=${m.top_front_height}`);
    if (m.shelf_count)           parts.push(`Shelf Count=${m.shelf_count}`);
    return parts.join(' | ');
  };

  // ── 5. Dimensions helper ──────────────────────────────────────────────────
  const dimStr = (m) => {
    if (!m) return '';
    const h = m.h === 34.5 ? '34 1/2' : m.h === 0.75 ? '3/4' : String(m.h);
    return `Width=${m.w} | Height=${h} | Depth=${m.d}`;
  };

  // ── 6. Format date ─────────────────────────────────────────────────────────
  const today = new Date();
  const dateStr = `${String(today.getMonth()+1).padStart(2,'0')}/${String(today.getDate()).padStart(2,'0')}/${today.getFullYear()}`;

  // ── 7. Init jsPDF ─────────────────────────────────────────────────────────
  const doc   = new jsPDF({ unit: 'pt', format: 'letter' });
  const PW    = 612;   // letter width  in pt
  const PH    = 792;   // letter height in pt
  const ML    = 45;    // margin left
  const MR    = 45;    // margin right
  const CW    = PW - ML - MR;  // content width
  const COL_CODE = ML;
  const COL_DESC = ML + 70;
  const COL_QTY  = PW - MR - 100;
  const COL_PRC  = PW - MR - 55;
  const COL_TOT  = PW - MR - 10;

  let y = 0;   // current Y cursor
  let pageNum = 1;
  let totalPages = 1; // updated at end

  // ── 8. Header ─────────────────────────────────────────────────────────────
  const drawHeader = () => {
    y = 40;

    // Logo area — "DOMAIN CABINETS / Stock & Custom"
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('DOMAIN CABINETS', ML, y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Stock & Custom', ML, y + 13);

    // "ITEM LIST" title top-right
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(100, 100, 160);
    doc.text('ITEM LIST', PW - MR, y, { align: 'right' });

    // Date / Order# block
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Date:', PW - MR - 95, y + 18);
    doc.text('Order#:', PW - MR - 45, y + 18);
    doc.setFont('helvetica', 'normal');
    doc.text(dateStr, PW - MR - 95, y + 29);
    doc.setFont('helvetica', 'bold');
    doc.text('—', PW - MR - 10, y + 29, { align: 'right' });

    y += 55;

    // Bill To / Ship To
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', ML, y);
    doc.text('Ship To:', ML + CW / 2, y);
    y += 4;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(ML, y, ML + CW, y);
    y += 12;

    // Bill To (left column)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    const billTo = [
      'John Smith',
      '2100 S Nebraska',
      'Ocean City, NJ 82688',
      'United States',
      '307-259-3906',
      'JohnSmith@gmail.com',
    ];
    const billToStartY = y;
    billTo.forEach(line => { doc.text(line, ML, y); y += 11; });

    // Ship To (right column) — bold blue like reference
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 160);
    const shipTo = [
      'John Smith',
      '1387 Morado Dr',
      'Ocean City, NJ 82688',
      'United States',
      '307-222-3333',
    ];
    shipTo.forEach((line, i) => {
      doc.text(line, ML + CW / 2, billToStartY + i * 11);
    });

    y += 10;

    // Payment / Shipping
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Payment Method:', ML, y);
    doc.text('Shipping Method:', ML + CW / 2, y);
    y += 4;
    doc.line(ML, y, ML + CW, y);
    y += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Wire Transfer', ML, y);
    doc.text('Free Destination Terminal Pickup', ML + CW / 2, y);
    y += 11;
    doc.text('Custom Orders 20k+', ML + CW / 2, y);
    y += 20;

    // Table header
    doc.setFillColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Code',        COL_CODE, y);
    doc.text('Description', COL_DESC, y);
    doc.text('Qty',   COL_QTY,  y, { align: 'right' });
    doc.text('Price', COL_PRC,  y, { align: 'right' });
    doc.text('Total', COL_TOT,  y, { align: 'right' });
    y += 4;
    doc.setLineWidth(1);
    doc.line(ML, y, ML + CW, y);
    y += 10;
  };

  // ── 9. Footer ─────────────────────────────────────────────────────────────
  const drawFooter = (pn, total) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`${pn}/${total}`, PW - MR, PH - 20, { align: 'right' });
  };

  // ── 10. New page helper ────────────────────────────────────────────────────
  const newPage = () => {
    drawFooter(pageNum, '?');
    doc.addPage();
    pageNum++;
    drawHeader();
  };

  // ── 11. Text wrap helper ───────────────────────────────────────────────────
  // Returns array of wrapped lines fitting within maxWidth at current fontSize
  const wrap = (text, maxWidth) =>
    doc.splitTextToSize(String(text ?? ''), maxWidth);

  // ── 12. Draw one line item row ─────────────────────────────────────────────
  const drawRow = (code, descLines, qty) => {
    // Calculate row height first
    const lineH   = 11;
    const rowH    = descLines.length * lineH + 14;

    if (y + rowH > PH - 50) newPage();

    const rowY = y;

    // Code
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    doc.text(code, COL_CODE, rowY + 11);

    // Description lines
    descLines.forEach((dl, i) => {
      const isFirst = i === 0;
      doc.setFont('helvetica', isFirst ? 'bold' : 'normal');
      doc.setFontSize(isFirst ? 9 : 8);
      doc.setTextColor(0, 0, 0);
      doc.text(dl, COL_DESC, rowY + 11 + i * lineH);
    });

    // Qty / Price / Total (N/A for visualizer quotes)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(String(qty ?? 1), COL_QTY,  rowY + 11, { align: 'right' });
    doc.text('N/A',            COL_PRC,  rowY + 11, { align: 'right' });
    doc.text('N/A',            COL_TOT,  rowY + 11, { align: 'right' });

    // Bottom divider
    y = rowY + rowH;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.4);
    doc.line(ML, y, ML + CW, y);
    y += 6;
  };

  // ── 13. Render all line items ──────────────────────────────────────────────
  drawHeader();

  if (lines.length === 0) {
    // No lines data — show a single summary row
    const summaryDesc = [
      'Custom Cabinetry Project',
      `[Cabinet Style: Collection=${col} | Door Color=${doorColor}`,
      ` Grain=${grain} | Box Material=${boxMat} | Drawer Box=${drawerBox}]`,
    ];
    drawRow('—', summaryDesc, 1);
  } else {
    lines.forEach((line) => {
      const m    = line?.msg || {};
      const prod = m?.product || {};
      const code = `(C)${m.product_code || ''}`;
      const name = `Custom ${prod.product_name || m.product_code || ''}`;

      // Dimensions
      const dim = dimStr(m);

      // Style string
      const style = styleStr(line);

      // Build description array
      const MAX_W = COL_QTY - COL_DESC - 10;
      const descLines = [
        name,
        ...wrap(`[Cabinet Dimension: ${dim} ]`, MAX_W),
        ...wrap(`[Cabinet Style: ${style} ]`,   MAX_W),
      ];

      drawRow(code, descLines, m.quantity ?? 1);
    });
  }

  // ── 14. Fix page numbers and save ─────────────────────────────────────────
  totalPages = pageNum;
  const totalPagesStr = String(totalPages);

  // Go back and stamp correct footers on each page
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawFooter(p, totalPagesStr);
  }

  doc.save('Item_List.pdf');
}
